"use client";

import {
  Briefcase,
  ClipboardCheck,
  FileText,
  Presentation,
  Video,
  ClipboardList,
  Award,
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/pagination";

const steps = [
  {
    step: "Step 1",
    title: "Opportunity Identification",
    description:
      "Engage with top recruiters & industry partners to source internships, OJLs & job roles",
    icon: Briefcase,
    colorScheme: "orange",
  },
  {
    step: "Step 2",
    title: "JD Screening & Approvals",
    description:
      "Review job descriptions aligned with academic standards & student eligibility",
    icon: ClipboardCheck,
    colorScheme: "blue",
  },
  {
    step: "Step 3",
    title: "Application via Digii Campus",
    description:
      "Students apply through the placement portal with real-time tracking & updates",
    icon: FileText,
    colorScheme: "orange",
  },
  {
    step: "Step 4",
    title: "Shortlisting & Pre-Placement Talks",
    description:
      "Recruiter-led sessions to prepare students & clarify expectations",
    icon: Presentation,
    colorScheme: "blue",
  },
  {
    step: "Step 5",
    title: "Interview Coordination",
    description: "Seamless scheduling, communication & interview logistics",
    icon: Video,
    colorScheme: "orange",
  },
  {
    step: "Step 6",
    title: "Interview Feedback & Results",
    description:
      "Recruiter feedback collected and results shared via email & platform",
    icon: ClipboardList,
    colorScheme: "blue",
  },
  {
    step: "Step 7",
    title: "Offer Letter & Joining",
    description: "Timely offer letters and smooth joining on designated dates",
    icon: Award,
    colorScheme: "orange",
  },
];

export default function Infographic() {
  return (
    <div className="bg-white relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-[#0475B3]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-72 h-72 bg-[#FFA201]/5 rounded-full blur-3xl"></div>

      <div className="page-container py-10 relative z-10">
        {/* Header */}
        <div className="w-full text-left mb-6">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black"
            style={{ fontFamily: "saans-bold" }}
          >
            Placement Process Flow
          </h2>

          <p className="text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed w-full md:w-[80%] lg:w-[70%]">
            Your edge at Medhavi Skills University where real-world learning,
            industry exposure, and future-ready skills come together.
          </p>
        </div>

        {/* Steps Grid - Desktop & Tablet */}
        <div className="hidden md:block">
          {/* First Row */}
          <div className="relative mb-6">
            <div className="grid grid-cols-4 gap-6">
              {steps.slice(0, 4).map((step, index) => {
                const Icon = step.icon;
                const isOrange = step.colorScheme === "orange";

                return (
                  <div key={index} className="group relative">
                    <div
                      className={`relative p-8 border transition-all duration-500 h-full min-h-[280px] flex flex-col overflow-hidden backdrop-blur-sm ${
                        isOrange
                          ? "border-[#FFA201]/20 bg-gradient-to-b from-[#FFA201]/10 to-white hover:border-[#FFA201]/40 hover:shadow-[0_20px_50px_rgba(255,162,1,0.15)]"
                          : "border-[#0475B3]/20 bg-gradient-to-b from-[#0475B3]/10 to-white hover:border-[#0475B3]/40 hover:shadow-[0_20px_50px_rgba(4,117,179,0.15)]"
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className={`w-20 h-20 rounded-full flex items-center justify-center group-hover:scale-105 group-hover:-translate-y-1 transition-all duration-500 mb-6 shadow-xl relative z-10 ${
                          isOrange
                            ? "bg-gradient-to-br from-[#FFA201] via-[#FF9500] to-[#FF8800]"
                            : "bg-gradient-to-br from-[#0475B3] via-[#0369A1] to-[#035A8F]"
                        }`}
                      >
                        <div className="absolute inset-0 rounded-full bg-white/10"></div>
                        <Icon
                          className="w-9 h-9 text-white relative z-10"
                          strokeWidth={2}
                        />
                      </div>

                      {/* Step Pill */}
                      <div
                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-5 w-fit relative z-10 ${
                          isOrange
                            ? "bg-gradient-to-r from-[#FFA201]/20 to-[#FFA201]/10 border border-[#FFA201]/30"
                            : "bg-gradient-to-r from-[#0475B3]/20 to-[#0475B3]/10 border border-[#0475B3]/30"
                        }`}
                      >
                        <span
                          className={`text-xs font-bold tracking-wider uppercase ${isOrange ? "text-[#FFA201]" : "text-[#0475B3]"}`}
                        >
                          {step.step}
                        </span>
                      </div>

                      {/* Content */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug group-hover:text-gray-800 transition-colors relative z-10">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed flex-grow relative z-10">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Second Row */}
          <div className="relative">
            <div className="grid grid-cols-4 gap-6">
              {steps.slice(4, 7).map((step, index) => {
                const Icon = step.icon;
                const isOrange = step.colorScheme === "orange";

                return (
                  <div key={index} className="group relative">
                    <div
                      className={`relative p-8 border transition-all duration-500 h-full min-h-[280px] flex flex-col overflow-hidden backdrop-blur-sm ${
                        isOrange
                          ? "border-[#FFA201]/20 bg-gradient-to-b from-[#FFA201]/10 to-white hover:border-[#FFA201]/40 hover:shadow-[0_20px_50px_rgba(255,162,1,0.15)]"
                          : "border-[#0475B3]/20 bg-gradient-to-b from-[#0475B3]/10 to-white hover:border-[#0475B3]/40 hover:shadow-[0_20px_50px_rgba(4,117,179,0.15)]"
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className={`w-20 h-20 rounded-full flex items-center justify-center group-hover:scale-105 group-hover:-translate-y-1 transition-all duration-500 mb-6 shadow-xl relative z-10 ${
                          isOrange
                            ? "bg-gradient-to-br from-[#FFA201] via-[#FF9500] to-[#FF8800]"
                            : "bg-gradient-to-br from-[#0475B3] via-[#0369A1] to-[#035A8F]"
                        }`}
                      >
                        <div className="absolute inset-0 rounded-full bg-white/10"></div>
                        <Icon
                          className="w-9 h-9 text-white relative z-10"
                          strokeWidth={2}
                        />
                      </div>

                      {/* Step Pill */}
                      <div
                        className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-5 w-fit relative z-10 ${
                          isOrange
                            ? "bg-gradient-to-r from-[#FFA201]/20 to-[#FFA201]/10 border border-[#FFA201]/30"
                            : "bg-gradient-to-r from-[#0475B3]/20 to-[#0475B3]/10 border border-[#0475B3]/30"
                        }`}
                      >
                        <span
                          className={`text-xs font-bold tracking-wider uppercase ${isOrange ? "text-[#FFA201]" : "text-[#0475B3]"}`}
                        >
                          {step.step}
                        </span>
                      </div>

                      {/* Content */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3 leading-snug group-hover:text-gray-800 transition-colors relative z-10">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed flex-grow relative z-10">
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
              {/* Empty cell for alignment */}
              <div className="hidden md:block"></div>
            </div>
          </div>
        </div>

        {/* Mobile Swiper Layout */}
        <div className="md:hidden">
          <Swiper
            modules={[]}
            spaceBetween={20}
            slidesPerView={1.25}
            loop={true}
            className="pb-12"
          >
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isOrange = step.colorScheme === "orange";

              return (
                <SwiperSlide key={index} className="h-auto">
                  <div className="relative h-full">
                    <div
                      className={`group relative p-5 border border-gray-200/50 hover:border-gray-300 hover:shadow-xl transition-all duration-300 h-full flex flex-col rounded-2xl ${
                        isOrange
                          ? "bg-gradient-to-b from-[#FFA201]/10 to-white"
                          : "bg-gradient-to-b from-[#0475B3]/10 to-white"
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 shrink-0 shadow-lg ${
                          isOrange ? "bg-[#FFA201]" : "bg-[#0475B3]"
                        }`}
                      >
                        <Icon className="w-7 h-7 text-white" strokeWidth={2} />
                      </div>

                      {/* Step Pill */}
                      <div
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-3 w-fit ${
                          isOrange
                            ? "bg-[#FFA201]/10 border border-[#FFA201]/20"
                            : "bg-[#0475B3]/10 border border-[#0475B3]/20"
                        }`}
                      >
                        <span
                          className={`text-xs font-semibold ${isOrange ? "text-[#FFA201]" : "text-[#0475B3]"}`}
                        >
                          {step.step}
                        </span>
                      </div>

                      {/* Content */}
                      <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
